import { Store } from '@ngrx/store';
import { Injectable, inject } from '@angular/core';
import { filter, first, map, switchMap, tap } from 'rxjs';

import { PositionStore } from './position.store';
import { PositionAction } from './position.action';
import { IPortfolioMarketData } from '../../modules/portfolio';
import { ILsData, LsSchemaType, LsSchemas } from '../../modules/shared/models';
import {
	CashSettlement,
	CashSettlementExport,
	CashSettlementRequest,
	PhysicalSettlement,
	PhysicalSettlementExport,
	PhysicalSettlementRequest,
	PositionExportType,
	SettlementType
} from '../../modules/option/models/option.model';
import { LsService, MarketDataService, NotificationService, NotificationType } from '../../modules/shared/services';
import { OptionService } from '@easy-lib/modules/option';
import { exportExcelFromArray } from '@easy-lib/tools/primptives-tool/array-tool';
import { DateHelper } from '@easy-lib/tools';

@Injectable({ providedIn: 'root' })
export class PositionFacade {
	store = inject(Store);
	lsService = inject(LsService);
	positionStore = inject(PositionStore);
	marketDataService = inject(MarketDataService);
	notifyService = inject(NotificationService);
	optionService = inject(OptionService);

	load = () => this.store.dispatch(PositionAction.load());
	notifyError = (message: string) => this.notifyService.alert(message, NotificationType.danger);
	notifySuccess = (message: string) => this.notifyService.alert(message, NotificationType.success);
	unSubscribeToMarketDataLs = () => this.lsService.unsubscribeSchema(LsSchemaType.RealtimePortfolio);

	createCashSettlement = (value: CashSettlementRequest) =>
		this.store.dispatch(PositionAction.createCashSettlement({ value }));
	createPhysicalSettlement = (value: PhysicalSettlementRequest) =>
		this.store.dispatch(PositionAction.createPhysicalSettlement({ value }));
	deleteSettlementRequest = (id: string, positionId: string, settlementType: SettlementType) =>
		this.store.dispatch(PositionAction.deleteSettlementRequest({ id, positionId, settlementType }));

	//---------------Subscribe to Ls service------------
	subscribeToMarketDataLs() {
		const marketDataFunc = (res: ILsData) => {
			const schema = LsSchemas.MobileRealTimePortfolioSchema;
			const data = {
				symbolIsin: res.inst,
				stateCode: res.val[schema.stateCode],
				lastTradedPrice: +(res.val[schema.LastTradedPrice] ?? 0),
				closingPrice: +(res.val[schema.ClosingPrice] ?? 0),
				lastTradedPricePercent: +(res.val[schema.lastTradedPriceVarPercent] ?? 0),
				feeOfPreviousDaysClosingPrice: +(res.val[schema.feeOfPreviousDaysClosingPrice] ?? 0),
				totalNumberOfSharesTraded: +(res.val[schema.TotalNumberOfSharesTraded] ?? 0)
			} as Partial<IPortfolioMarketData>;
			this.store.dispatch(PositionAction.upsertMarketData({ value: data }));
		};
		return this.positionStore.PositionIsins$.pipe(
			filter(isins => !!isins?.length),
			tap(isins =>
				this.lsService.subscribeToLsIfNotAvailable(LsSchemaType.RealtimePortfolio, isins, marketDataFunc)
			)
		);
	}
	getMarketDataWhenLsIsNotConnected() {
		const marketDataFunc = () => {
			return this.positionStore.PositionIsins$.pipe(
				first(),
				switchMap(isins => this.marketDataService.GetPortfolioData(isins)),
				map(marketItems =>
					marketItems.map(item => {
						const { symbolISIN, priceVar, ...res } = item;
						return {
							...res,
							symbolIsin: symbolISIN,
							lastTradedPricePercent: priceVar
						} as Partial<IPortfolioMarketData>;
					})
				),
				map(marketItems => this.store.dispatch(PositionAction.setMarketData({ values: marketItems })))
			);
		};
		return this.lsService.callFunctionWhenLsIsNotConnected(marketDataFunc);
	}

	//-----------------Export to Excel------------------
	exportCashSettlementToExcel(positionId: string) {
		this.optionService
			.getCashSettlements(positionId)
			.pipe(
				map(res => this.makeExportData(res, CashSettlementExport)),
				tap(({ headers, values }) => exportExcelFromArray(headers, values, 'cashSettlement-export'))
			)
			.subscribe();
	}
	exportPhysicalSettlementToExcel(positionId: string) {
		this.optionService
			.getPhysicalSettlements(positionId)
			.pipe(
				map(res => this.makeExportData(res, PhysicalSettlementExport)),
				tap(({ headers, values }) => exportExcelFromArray(headers, values, 'physicalSettlement-export'))
			)
			.subscribe();
	}
	private makeExportData(items: PhysicalSettlement[] | CashSettlement[], exportColumns: PositionExportType[]) {
		const headers = exportColumns.map(t => ({
			title: t.title,
			width: t.width
		}));
		const values = items
			.map(a => {
				return { ...a, createDate: DateHelper.getFormattedJDateTime(new Date(a.createDate)) };
			})
			.map(data => {
				const resultData = {};
				exportColumns.forEach(info => {
					if (typeof data[info.fieldName] == 'boolean')
						resultData[info.fieldName] = BooleanToPersian[+data[info.fieldName]];
					else resultData[info.fieldName] = data[info.fieldName];
				});
				return resultData;
			});
		return { headers, values };
	}
}
export const BooleanToPersian: Record<0 | 1, string> = {
	0: 'خیر',
	1: 'بلی'
};
